import {Pipe, PipeTransform} from '@angular/core'
import {Operations} from '@common/global-constants'
import {Activity} from '@models/activity'
import {Order} from '@models/order/order'
import {ApiService} from '@service/api.service'

/**
 * Pipe asíncrono que verifica si un dato está sincronizado con el servidor.
 *
 * @description
 * Determina si una orden o actividad ya fue sincronizada verificando si existe
 * un cambio pendiente de INSERT en la cola de sincronización. Retorna true si
 * está sincronizado (no hay cambio pendiente). Pipe asíncrono que debe usarse
 * con el async pipe de Angular.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * En templates:
 * ```html
 * <div *ngIf="(order | issync:'orders' | async)">Sincronizado</div>
 * ```
 *
 * ### Casos de uso
 *
 * ```html
 * <!-- Indicador de sincronización -->
 * <ion-badge *ngIf="(order | issync:'orders' | async)"
 *            color="success">
 *   Sincronizado
 * </ion-badge>
 *
 * <!-- Mostrar estado no sincronizado -->
 * <ion-icon *ngIf="!(activity | issync:'activities' | async)"
 *           name="cloud-offline"
 *           color="warning">
 * </ion-icon>
 *
 * <!-- Lista con indicadores -->
 * <ion-item *ngFor="let order of orders">
 *   <ion-label>{{ order.number }}</ion-label>
 *   <ion-icon slot="end"
 *             *ngIf="(order | issync:'orders' | async)"
 *             name="checkmark-circle"
 *             color="success">
 *   </ion-icon>
 * </ion-item>
 * ```
 *
 * @example
 * ```typescript
 * // En un componente
 * const pipe = new IsSyncPipe(apiService);
 * const order = { id: '123' };
 *
 * pipe.transform(order, 'orders').then(isSynced => {
 *   isSynced; // → true si está sincronizado, false si hay cambios pendientes
 * });
 * ```
 */
@Pipe({
  name: 'issync',
})
export class IsSyncPipe implements PipeTransform {
  constructor(private readonly apiService: ApiService) {}

  /**
   * Verifica de forma asíncrona si un dato está sincronizado.
   *
   * @param data - Orden o actividad a verificar
   * @param collection - Nombre de la colección ('orders', 'activities', etc.)
   * @returns Promise que resuelve a true si está sincronizado, false si hay cambios pendientes
   *
   * @example
   * ```typescript
   * const order = { id: '123' };
   * await transform(order, 'orders') // returns true/false
   * ```
   */
  async transform(data: Order | Activity, collection: string): Promise<boolean> {
    const existData = await this.apiService.existChange(`${collection}${data.id}`, Operations.INSERT)
    return !existData
  }
}
